import React from 'react';
import { motion } from 'framer-motion';
import recfull from './assets/images/spotifyrec_full.png';
import recsearch from './assets/images/spotifyrec_search.png';
import top4 from './assets/images/spotifyrec_top4.png';

const SectionTitle = ({ children }) => (
  <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
    <span className="text-secondary">/</span> {children}
  </h2>
);

export default function SpotifyRecommendations({ onBack }) {
  return (
    <div className="max-w-5xl mx-auto px-6 py-20">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-secondary hover:text-white font-mono text-sm mb-8"
      >
        ← Back to portfolio
      </button>

      <SectionTitle>Spotify Personal Recommendation Engine</SectionTitle>

      <div> 
        <img
          src={recsearch}
          alt="Full interface preview"
          className="w-full h-96 object-cover rounded-2xl mb-12"
        />
      </div>

      <div className="grid gap-12 lg:grid-cols-[1fr_0.9fr] mb-14">
        <div className="space-y-6 text-slate-400">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-lg leading-8"
          >
            This project is a personalized music recommendation engine built to help listeners discover songs aligned with their own taste. It combines Spotify and Last.fm listening data with a similarity model that ranks tracks based on audio features, metadata, and listening behavior.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg leading-8"
          >
            The interface is built in React with a clean card-based experience. Users can search for a seed track, view recommended songs, and explore why each recommendation was chosen via feature similarity and personal listening affinity.
          </motion.p>
          <div className="rounded-3xl border border-slate-700 bg-slate-900 p-6">
            <h3 className="text-xl font-semibold text-white mb-3">Key details</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-400">
              <li>Custom similarity model leveraging audio features and personal listening trends.</li>
              <li>Integrated Spotify and Last.fm APIs for rich metadata and playback context.</li>
              <li>Responsive React UI optimized for curated music browsing.</li>
            </ul>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="rounded-3xl overflow-hidden border border-slate-700 bg-slate-800">
            <img
              src={top4}
              alt="Project interface preview"
              className="w-full h-80 object-cover"
            />
          </div>
          <div className="rounded-3xl border border-slate-700 bg-slate-900 p-6">
            <p className="text-slate-400 text-sm">
              This page layout uses an image panel and summary card to keep the experience polished and easy to scan.
            </p>
          </div>
        </motion.div>
      </div>

      <div className="rounded-3xl border border-slate-700 bg-slate-900 p-8 space-y-6">
        <img
          src={recfull}
          alt="Full interface preview"
          className="w-full h-full object-cover rounded-2xl mb-12"
        />
        </div>

      <div className="rounded-3xl border border-slate-700 bg-slate-800 p-8">
        <h3 className="text-2xl font-bold text-white mb-4">What I built</h3>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h4 className="text-white font-semibold mb-3">Recommendation engine</h4>
            <p className="text-slate-400 leading-7">
              I designed an engine that compares tracks using cosine similarity across audio feature vectors and listening frequency. The result is recommendations that feel personally tailored instead of generic playlist picks.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">User experience</h4>
            <p className="text-slate-400 leading-7">
              The UI focuses on fast discovery, showing song metadata, cover art, and why each track was recommended. This page mirrors the portfolio's polished aesthetic with motion and clear typography.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
